import { css, html, LitElement } from 'lit'
import { property } from 'lit/decorators.js'
import { safeCustomElement } from '@/utilities/custom-element'
import type { AccordionItemColor, AccordionItemStyle } from './types'

@safeCustomElement('mdc-accordion-item')
export class MdcAccordionItem extends LitElement {
  @property({ type: String }) color: AccordionItemColor = 'default'
  @property({ type: String }) variant: AccordionItemStyle = 'subtle'
  @property({ type: String }) id = ''
  @property({ type: String }) label = ''
  @property({ type: Boolean, reflect: true, attribute: 'is-opened' }) isOpened = false

  static styles = css`
		:host {
			font-family: var(--typography-font-family-sans);
			/* Accordion Item Design Tokens */
			--mdc-accordion-item-font-family-subtle: var(--typography-font-family-sans);
			--mdc-accordion-item-font-family-bold: var(--typography-font-family-sans);
			--mdc-accordion-item-font-size-subtle: var(--typography-font-size-075, var(--text-075, 0.75rem));
			--mdc-accordion-item-font-size-bold: var(--typography-font-size-087-and-half, var(--text-087-and-half, 0.875rem));
			--mdc-accordion-item-font-weight-subtle: 450;
			--mdc-accordion-item-font-weight-bold: 600;
			--mdc-accordion-item-line-height-subtle: 1.3;
			--mdc-accordion-item-line-height-bold: 1.2;
			--mdc-accordion-item-letter-spacing-bold: -0.01em;
			--mdc-accordion-item-icon-size: 1rem;
		}

		:host {
			display: block;
		}

		.mdc-accordion-item {
			display: block;
		}

		.mdc-accordion-item__trigger {
			cursor: pointer;
			display: flex;
			padding: var(--spacing-075, 0.75rem) var(--spacing-050, 0.5rem) var(--spacing-075, 0.75rem) 0;
			gap: var(--spacing-100, 1rem);
			align-items: center;
			justify-content: space-between;
			width: 100%;
			background: none;
			border: none;
			text-align: left;
		}

		.mdc-accordion-item__label {
			display: inline-flex;
			align-items: center;
			gap: var(--spacing-050, 0.5rem);
		}

		.mdc-accordion-item__trigger > svg,
		.mdc-accordion-item__trigger mdc-icon {
			display: flex;
			align-items: center;
			justify-content: center;
			flex-shrink: 0;
			width: var(--mdc-accordion-item-icon-size);
			height: var(--mdc-accordion-item-icon-size);
			color: currentColor;
			transition: transform 300ms ease;
			transform-origin: center;
			pointer-events: none;
		}

		.mdc-accordion-item__trigger mdc-icon > span {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 100%;
			height: 100%;
		}

		.mdc-accordion-item__trigger mdc-icon svg {
			display: block;
			width: 100%;
			height: 100%;
		}

		:host([is-opened]) .mdc-accordion-item__trigger > svg,
		:host([is-opened]) .mdc-accordion-item__trigger mdc-icon {
			transform: rotate(180deg);
		}

		.mdc-accordion-item__wrapper {
			display: grid;
			grid-template-rows: 0fr;
			transition: grid-template-rows 300ms ease;
			overflow: hidden;
		}

		:host([is-opened]) .mdc-accordion-item__wrapper {
			grid-template-rows: 1fr;
		}

		.mdc-accordion-item__content {
			min-height: 0;
			overflow: hidden;
		}

		.mdc-accordion-item__content-inner {
			min-height: 0;
			padding-bottom: var(--spacing-075, 0.75rem);
			padding-top: var(--spacing-050, 0.5rem);
		}

		.mdc-accordion-item--color-default .mdc-accordion-item__trigger > svg,
		.mdc-accordion-item--color-default .mdc-accordion-item__trigger mdc-icon {
			/* Uses currentColor to inherit from parent, adapts to dark mode */
		}

		.mdc-accordion-item--color-default .mdc-accordion-item__label {
			color: var(--color-foreground-primary, var(--color-functional-gray-black));
		}

		.mdc-accordion-item--color-default .mdc-accordion-item__content {
			border-bottom: 1px solid var(--color-border-primary, var(--color-functional-gray-75));
		}

		.mdc-accordion-item--color-inverse .mdc-accordion-item__trigger {
			color: var(--color-foreground-inverse-primary, var(--color-functional-gray-white));
		}

		.mdc-accordion-item--color-inverse .mdc-accordion-item__label {
			color: var(--color-foreground-inverse-secondary, var(--color-functional-gray-200));
		}

		.mdc-accordion-item--color-inverse .mdc-accordion-item__content {
			border-bottom: 1px solid var(--color-border-inverse-secondary, var(--color-functional-gray-600));
		}

		.mdc-accordion-item--style-subtle .mdc-accordion-item__label {
			font-family: var(--mdc-accordion-item-font-family-subtle);
			font-size: var(--mdc-accordion-item-font-size-subtle);
			font-weight: var(--mdc-accordion-item-font-weight-subtle);
			line-height: var(--mdc-accordion-item-line-height-subtle);
		}

		.mdc-accordion-item--style-bold .mdc-accordion-item__label {
			font-family: var(--mdc-accordion-item-font-family-bold);
			font-size: var(--mdc-accordion-item-font-size-bold);
			font-weight: var(--mdc-accordion-item-font-weight-bold);
			line-height: var(--mdc-accordion-item-line-height-bold);
			letter-spacing: var(--mdc-accordion-item-letter-spacing-bold);
		}
	`

  connectedCallback() {
    super.connectedCallback()
  }

  firstUpdated() {
    // Component is fully initialized after first render
    // The @click directive should handle event binding automatically
  }

  updated(changedProperties: Map<PropertyKey, unknown>) {
    super.updated(changedProperties)
    // Manually ensure the is-opened attribute is set for CSS
    // reflect: true should handle this, but ensuring it works
    if (changedProperties.has('isOpened')) {
      if (this.isOpened) {
        this.setAttribute('is-opened', '')
      } else {
        this.removeAttribute('is-opened')
      }
    }
  }

  private handleToggle(e: Event): void {
    e.preventDefault()
    this.isOpened = !this.isOpened
    this.dispatchEvent(
      new CustomEvent('toggle', {
        bubbles: true,
        composed: true,
        detail: { isOpened: this.isOpened },
      })
    )
  }

  render() {
    const className = `mdc-accordion-item mdc-accordion-item--color-${this.color} mdc-accordion-item--style-${this.variant}${this.isOpened ? ' mdc-accordion-item--opened' : ''}`

    return html`
			<div class=${className}>
				<button
					type="button"
					class="mdc-accordion-item__trigger"
					@click=${this.handleToggle}
					aria-expanded=${this.isOpened ? 'true' : 'false'}
					aria-controls="content-panel-${this.id}"
					id="trigger-${this.id}"
				>
					<span class="mdc-accordion-item__label"><slot name="label">${this.label}</slot></span>
					<mdc-icon name="chevron-down"></mdc-icon>
				</button>
				<div
					class="mdc-accordion-item__wrapper"
					id="content-panel-${this.id}"
					role="region"
					aria-labelledby="trigger-${this.id}"
				>
					<div class="mdc-accordion-item__content">
						<div class="mdc-accordion-item__content-inner">
							<slot></slot>
						</div>
					</div>
				</div>
			</div>
		`
  }
}
